// Energetic Alignment — content const (EA0).
// Source of truth: docs/ENERGETIC_ALIGNMENT_CONTENT.md (49 reflections, verbatim) +
// docs/ENERGETIC_ALIGNMENT_PLAN.md (model §1, scale §1).
//
// A 7×7 bidirectional "centered-flow" assessment (twin of Life Balance): 1–7 scale
// where 4 = Ideal (1–3 below ideal / deficient, 5–7 above ideal / excessive).
// Domains lead with the PSYCHOLOGICAL name; the chakra is a subtle note.

/** Fixed assessment row id (type 'energetic'). */
export const ENERGETIC_ID = 'a1000000-0000-0000-0000-000000000014';

/** The centered/ideal rating. */
export const ENERGETIC_IDEAL = 4;

/** 1–7 scale labels (4 = ideal/centered). */
export const ENERGETIC_SCALE: { value: number; label: string; band: 'deficient' | 'centered' | 'excessive' }[] = [
  { value: 1, label: 'Far Below Ideal', band: 'deficient' },
  { value: 2, label: 'Below Ideal', band: 'deficient' },
  { value: 3, label: 'Slightly Below Ideal', band: 'deficient' },
  { value: 4, label: 'Ideal', band: 'centered' },
  { value: 5, label: 'Slightly Above Ideal', band: 'excessive' },
  { value: 6, label: 'Above Ideal', band: 'excessive' },
  { value: 7, label: 'Far Above Ideal', band: 'excessive' },
];

/** Label for a 1–7 rating (used by the quiz selected-state + intro legend). */
export const energeticScaleLabel = (value: number): string =>
  ENERGETIC_SCALE.find(s => s.value === value)?.label ?? '';

export interface EnergeticParameter {
  name: string;
  reflection: string; // first-person affirmation, rated 1–7 against lived truth
}

export interface EnergeticDomain {
  id: string; // 'foundational' …
  order: number; // 1–7
  name: string; // psychological-primary name
  chakra: string; // subtle note, e.g. 'Root'
  essence: string;
  color: string; // chakra-mapped hue
  parameters: EnergeticParameter[]; // 7
}

export const ENERGETIC_DOMAINS: EnergeticDomain[] = [
  {
    id: 'foundational',
    order: 1,
    name: 'Foundational',
    chakra: 'Root',
    essence: 'grounding, safety, stability, the body and material life',
    color: '#ef4444',
    parameters: [
      { name: 'Sense of Safety', reflection: 'I am grounded in an unshakable sense of safety that flows through every space I inhabit.' },
      { name: 'Stability', reflection: 'I am a calm center in the ever-changing tides of life, steady and unwavering.' },
      { name: 'Discipline & Structure', reflection: 'I am a harmonious architect of my routines, weaving structure with freedom effortlessly.' },
      { name: 'Physical Vitality', reflection: 'I am a vessel of radiant energy, moving with strength and vitality in every moment.' },
      { name: 'Groundedness', reflection: 'I am deeply rooted in the present, like an ancient tree that feels the pulse of the earth beneath me.' },
      { name: 'Material Relationship', reflection: 'I am content and abundant, free from clinging to possessions yet grateful for all I receive' },
      { name: 'Presence', reflection: 'I am fully awake to the magic of this moment, undistracted by past echoes or future whispers.' },
    ],
  },
  {
    id: 'emotional',
    order: 2,
    name: 'Emotional',
    chakra: 'Sacral',
    essence: 'feeling, pleasure, creativity, adaptability, intimacy',
    color: '#f97316',
    parameters: [
      { name: 'Emotional Sensitivity', reflection: 'I am open to feeling the full spectrum of my emotions, allowing them to flow through me like gentle waves.' },
      { name: 'Pleasure & Enjoyment', reflection: 'I am fully present in the joy and simple pleasures of life, honoring my right to feel delight.' },
      { name: 'Adaptability', reflection: 'I am fluid and adaptable, moving gracefully with the unexpected rhythms of life.' },
      { name: 'Creative Flow', reflection: 'I am a living channel of creative energy, freely expressing what wants to be born through me.' },
      { name: 'Relationship Boundaries', reflection: 'I am clear and kind in the boundaries I hold, honoring both my own space and the space of others.' },
      { name: 'Passion & Desire', reflection: 'I am guided by my passions and desires, allowing them to inspire me without consuming me.' },
      { name: 'Intimacy & Connection', reflection: 'I am open to deep emotional and physical connection, trusting in the dance of true intimacy.' },
    ],
  },
  {
    id: 'disciplinary',
    order: 3,
    name: 'Disciplinary',
    chakra: 'Solar Plexus',
    essence: 'will, self-esteem, drive, assertiveness, discipline',
    color: '#eab308',
    parameters: [
      { name: 'Willpower', reflection: 'I am a steady force of will, moving forward with clarity and quiet strength.' },
      { name: 'Self-Esteem', reflection: 'I am secure in my worth, shining confidently from my inner light.' },
      { name: 'Responsibility', reflection: 'I am fully responsible for my choices and actions, embracing them as reflections of my growth.' },
      { name: 'Assertiveness', reflection: 'I am able to speak my truth and stand my ground with calm courage.' },
      { name: 'Personal Drive', reflection: 'I am deeply motivated, guided by a purposeful flame that keeps me moving forward.' },
      { name: 'Self-Discipline', reflection: 'I am committed to my path, balancing freedom and discipline with mindful intention.' },
      { name: 'Motivation', reflection: 'I am consistently fueled by an inner spark, turning intention into action with ease.' },
    ],
  },
  {
    id: 'relational',
    order: 4,
    name: 'Relational',
    chakra: 'Heart',
    essence: 'love, empathy, compassion, trust, connection',
    color: '#22c55e',
    parameters: [
      { name: 'Empathy', reflection: 'I am attuned to the feelings of others, sensing their joys and sorrows with an open heart.' },
      { name: 'Compassion', reflection: 'I am a wellspring of compassion, offering kindness and gentle understanding to myself and those around me.' },
      { name: 'Trust', reflection: 'I am open to trust, allowing connections to deepen as I release fear and embrace vulnerability.' },
      { name: 'Self-Love', reflection: 'I am tender and loving toward myself, honoring my own worth without condition.' },
      { name: 'Openness', reflection: 'I am open-hearted and receptive, allowing life and relationships to flow into me naturally.' },
      { name: 'Connection to Others', reflection: 'I am deeply connected to others, weaving bonds rooted in authenticity and love.' },
      { name: 'Acceptance', reflection: 'I am accepting of others as they are, and I welcome the full humanity in myself and everyone I meet.' },
    ],
  },
  {
    id: 'expressive',
    order: 5,
    name: 'Expressive',
    chakra: 'Throat',
    essence: 'voice, truth, communication, authentic expression',
    color: '#3b82f6',
    parameters: [
      { name: 'Authentic Expression', reflection: 'I am true to my voice, expressing my deepest truths without fear or hesitation.' },
      { name: 'Communication Clarity', reflection: 'I am clear and intentional in my words, allowing my thoughts to flow with ease and understanding.' },
      { name: 'Listening Ability', reflection: 'I am an open listener, fully present and receptive to the voices and stories of others.' },
      { name: 'Honesty', reflection: 'I am devoted to honesty, honoring the power of truth in every moment.' },
      { name: 'Creative Voice', reflection: 'I am a vessel for creative expression, letting my unique voice move freely into the world.' },
      { name: 'Speaking Confidence', reflection: 'I am confident in sharing my words, standing firmly in the light of my self-expression.' },
      { name: 'Rhythm & Flow', reflection: 'I am in harmony with my natural rhythm, allowing my words and expressions to flow like a gentle river.' },
    ],
  },
  {
    id: 'intuitive',
    order: 6,
    name: 'Intuitive',
    chakra: 'Third Eye',
    essence: 'intuition, insight, imagination, vision, clarity',
    color: '#6366f1',
    parameters: [
      { name: 'Intuition', reflection: 'I am deeply guided by my intuition, trusting the quiet whispers of my inner knowing.' },
      { name: 'Insight', reflection: 'I am able to see beyond the surface, recognizing hidden truths and deeper meanings.' },
      { name: 'Imagination', reflection: 'I am free in my imagination, exploring new realms and creative possibilities without limits.' },
      { name: 'Mental Clarity', reflection: 'I am clear-minded and focused, navigating life with steady and open awareness.' },
      { name: 'Memory & Recall', reflection: 'I am able to access and honor my memories, holding the lessons and moments they carry.' },
      { name: 'Vision & Direction', reflection: 'I am guided by a clear vision, moving forward with a sense of purpose and higher direction.' },
      { name: 'Perception of Reality', reflection: 'I am able to see reality with clarity and depth, perceiving the world with both wisdom and wonder.' },
    ],
  },
  {
    id: 'universal',
    order: 7,
    name: 'Universal',
    chakra: 'Crown',
    essence: 'meaning, purpose, wisdom, higher awareness, unity',
    color: '#a855f7',
    parameters: [
      { name: 'Spiritual Connection', reflection: 'I am deeply connected to the divine essence within and around me, feeling guided by a higher presence.' },
      { name: 'Sense of Purpose', reflection: 'I am living with a clear sense of purpose, allowing my life to unfold with meaning and intention.' },
      { name: 'Open-Mindedness', reflection: 'I am open to infinite perspectives, embracing the mysteries and possibilities of existence.' },
      { name: 'Wisdom', reflection: 'I am rooted in inner wisdom, allowing my choices to be guided by deep understanding and clarity.' },
      { name: 'Healthy Detachment', reflection: 'I am able to let go with grace, trusting the flow of life without clinging or resistance.' },
      { name: 'Higher Awareness', reflection: 'I am aware of my inner landscape and the greater patterns that weave through my life and the universe.' },
      { name: 'Unity Consciousness', reflection: 'I am one with all life, experiencing a profound sense of oneness and universal love.' },
    ],
  },
];

// ── Derived lookups ────────────────────────────────────────────────────────────
export const ENERGETIC_DOMAIN_IDS: string[] = ENERGETIC_DOMAINS.map(d => d.id);

export const energeticDomainById: Record<string, EnergeticDomain> = Object.fromEntries(
  ENERGETIC_DOMAINS.map(d => [d.id, d]),
);

/** Domain name → id (the seed stores `dimension` as the domain NAME, like Life Balance). */
export const energeticDomainByName: Record<string, EnergeticDomain> = Object.fromEntries(
  ENERGETIC_DOMAINS.map(d => [d.name, d]),
);
